import {useCallback, useMemo, type SyntheticEvent} from 'react'
import {Box, IconButton, Typography, Checkbox, FormControlLabel} from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import {useTranslation} from 'react-i18next'
import {useAppDispatch} from '../../../common/store'
import {removeSprite3D, updateSprite3D, updateSprite3DMaterial} from '../../../common/components/store/scenesSlice'
import {sealUndo} from '../../../common/undoable'
import {useAppTokens} from '../../theme'
import type {ThemeTokens} from '../../theme'
import type {SceneSprite3D, Sprite3DMaterial} from '../../../common/types/spriteTypes'
import {useAuth} from '../../auth/useAuth'
import {uploadAsset} from '../../../common/api/uploadAsset'
import {cleanupReplacedAsset} from '../../../common/api/cleanupReplacedAsset'
import {loadImageAsDataUrl, measureImageAspect} from '../../../common/utils/spriteUpload'
import {DEFAULT_SPRITE_IMAGE_URL} from '../../../common/constants/defaultAssets'
import SpriteDropZone from './SpriteDropZone'
import PositionPad3D from '../sharedWidgets/PositionPad3D'
import RotationSphere from '../sharedWidgets/RotationSphere'
import SliderRow from '../sharedWidgets/SliderRow'

interface Sprite3DItemProps {
  sprite: SceneSprite3D
}

const Sprite3DItem = ({sprite}: Sprite3DItemProps) => {
  const {t} = useTranslation()
  const dispatch = useAppDispatch()
  const {user} = useAuth()
  const tokens = useAppTokens()
  const sx = useMemo(() => buildStyles(tokens), [tokens])

  const handleFieldChange = useCallback(
    (field: keyof SceneSprite3D, value: string | number | boolean | null) => {
      dispatch(updateSprite3D({id: sprite.id, changes: {[field]: value}}))
    },
    [dispatch, sprite.id],
  )

  const handleSealUndo = useCallback(() => {
    dispatch(sealUndo())
  }, [dispatch])

  const handleMaterialChange = useCallback(
    (prop: keyof Sprite3DMaterial, value: number) => {
      dispatch(updateSprite3DMaterial({id: sprite.id, changes: {[prop]: value}}))
    },
    [dispatch, sprite.id],
  )

  const handleApplyFile = useCallback(
    async (file: File) => {
      let imageKey: string | null = null
      let imageUrl: string
      if (user) {
        const result = await uploadAsset(file)
        imageKey = result.key
        imageUrl = result.url
        cleanupReplacedAsset(sprite.imageKey)
      } else {
        imageUrl = await loadImageAsDataUrl(file)
      }
      const imageAspect = await measureImageAspect(imageUrl)
      dispatch(updateSprite3D({id: sprite.id, changes: {imageKey, imageUrl, imageAspect}}))
      dispatch(sealUndo())
    },
    [dispatch, sprite.id, sprite.imageKey, user],
  )

  const handleScaleChange = useCallback((v: number) => handleFieldChange('scale', v), [handleFieldChange])

  const handlePosX = useCallback((v: number) => handleFieldChange('x', v), [handleFieldChange])
  const handlePosY = useCallback((v: number) => handleFieldChange('y', v), [handleFieldChange])
  const handlePosZ = useCallback((v: number) => handleFieldChange('z', v), [handleFieldChange])

  const handleRotX = useCallback((v: number) => handleFieldChange('rotationX', v), [handleFieldChange])
  const handleRotY = useCallback((v: number) => handleFieldChange('rotationY', v), [handleFieldChange])
  const handleRotZ = useCallback((v: number) => handleFieldChange('rotationZ', v), [handleFieldChange])

  const handleToggleUnlit = useCallback(
    (_: SyntheticEvent, checked: boolean) => {
      handleFieldChange('unlit', checked)
      dispatch(sealUndo())
    },
    [handleFieldChange, dispatch],
  )

  const handleMetalness = useCallback((v: number) => handleMaterialChange('metalness', v), [handleMaterialChange])
  const handleRoughness = useCallback((v: number) => handleMaterialChange('roughness', v), [handleMaterialChange])
  const handleClearcoat = useCallback((v: number) => handleMaterialChange('clearcoat', v), [handleMaterialChange])
  const handleClearcoatRoughness = useCallback(
    (v: number) => handleMaterialChange('clearcoatRoughness', v),
    [handleMaterialChange],
  )
  const handleReflectivity = useCallback((v: number) => handleMaterialChange('reflectivity', v), [handleMaterialChange])

  const handleRemove = useCallback(() => dispatch(removeSprite3D(sprite.id)), [dispatch, sprite.id])

  return (
    <Box sx={sx.root}>
      <Box sx={sx.headerRow}>
        <Box sx={sx.dropZoneSlot}>
          <SpriteDropZone
            imageUrl={sprite.imageUrl}
            isDefaultImage={sprite.imageUrl === DEFAULT_SPRITE_IMAGE_URL}
            onApplyFile={handleApplyFile}
          />
        </Box>
        <IconButton onClick={handleRemove} size="small" sx={sx.deleteBtn} title={t('sprite3d.remove')}>
          <DeleteIcon sx={{fontSize: 16}} />
        </IconButton>
      </Box>

      <SliderRow
        label={t('sprite3d.scale')}
        value={sprite.scale}
        min={1}
        max={200}
        step={0.5}
        unit=""
        onChange={handleScaleChange}
        onChangeCommitted={handleSealUndo}
      />

      <FormControlLabel
        control={<Checkbox checked={sprite.unlit} onChange={handleToggleUnlit} size="small" sx={sx.checkbox} />}
        label={<Typography sx={sx.label}>{t('sprite3d.unlit')}</Typography>}
        sx={sx.checkboxLabel}
      />

      {!sprite.unlit && (
        <>
          <Typography sx={sx.sectionLabel}>{t('sprite3d.material')}</Typography>

          <SliderRow
            label={t('device.materials.metalness')}
            value={sprite.material.metalness}
            min={0}
            max={1}
            step={0.01}
            unit=""
            onChange={handleMetalness}
            onChangeCommitted={handleSealUndo}
          />

          <SliderRow
            label={t('device.materials.roughness')}
            value={sprite.material.roughness}
            min={0}
            max={1}
            step={0.01}
            unit=""
            onChange={handleRoughness}
            onChangeCommitted={handleSealUndo}
          />

          <SliderRow
            label={t('device.materials.clearcoat')}
            value={sprite.material.clearcoat}
            min={0}
            max={1}
            step={0.01}
            unit=""
            onChange={handleClearcoat}
            onChangeCommitted={handleSealUndo}
          />

          <SliderRow
            label={t('device.materials.clearcoatRoughness')}
            value={sprite.material.clearcoatRoughness}
            min={0}
            max={1}
            step={0.01}
            unit=""
            onChange={handleClearcoatRoughness}
            onChangeCommitted={handleSealUndo}
          />

          <SliderRow
            label={t('device.materials.reflectivity')}
            value={sprite.material.reflectivity}
            min={0}
            max={1}
            step={0.01}
            unit=""
            onChange={handleReflectivity}
            onChangeCommitted={handleSealUndo}
          />
        </>
      )}

      <Typography sx={sx.sectionLabel}>{t('sprite3d.position')}</Typography>
      <PositionPad3D
        x={sprite.x}
        y={sprite.y}
        z={sprite.z}
        onChangeX={handlePosX}
        onChangeY={handlePosY}
        onChangeZ={handlePosZ}
        onDragEnd={handleSealUndo}
      />

      <Typography sx={sx.sectionLabel}>{t('sprite3d.rotation')}</Typography>
      <RotationSphere
        rotationX={sprite.rotationX}
        rotationY={sprite.rotationY}
        rotationZ={sprite.rotationZ}
        onChangeX={handleRotX}
        onChangeY={handleRotY}
        onChangeZ={handleRotZ}
        onDragEnd={handleSealUndo}
      />
    </Box>
  )
}

const buildStyles = (tokens: ThemeTokens) => ({
  root: {
    padding: '8px',
    marginBottom: '8px',
    borderRadius: '6px',
    backgroundColor: 'rgba(255, 255, 255, 0.04)',
    border: `1px solid ${tokens.accordion.border}`,
  },
  headerRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '4px',
    marginBottom: '4px',
  },
  dropZoneSlot: {
    flex: 1,
    minWidth: 0,
  },
  deleteBtn: {
    color: tokens.text.muted,
    marginTop: '2px',
    '&:hover': {color: '#ef5350'},
  },
  label: {
    fontSize: 11,
    color: tokens.text.secondary,
    whiteSpace: 'nowrap' as const,
  },
  sectionLabel: {
    fontSize: 11,
    fontWeight: 600,
    color: tokens.text.muted,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.04em',
    marginTop: '8px',
    marginBottom: '4px',
  },
  checkboxLabel: {
    marginTop: '4px',
    marginBottom: '0px',
    marginLeft: '-4px',
  },
  checkbox: {
    color: tokens.text.muted,
    '&.Mui-checked': {
      color: tokens.text.primary,
    },
    padding: '4px',
  },
})

export default Sprite3DItem
